import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Key,
  Users,
  Lock,
  CheckCircle,
  RefreshCw,
  Search
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Agrupar permissões por categoria para melhor visualização
const CATEGORIAS_PERMISSOES = {
  "Dashboard": ["acessar_dashboard", "visualizar_metricas", "exportar_dashboard"],
  "Clientes": ["acessar_clientes", "cadastrar_cliente", "editar_cliente", "excluir_cliente", "importar_clientes"],
  "Monitoramento Contrato": ["acessar_monitoramento_contrato", "editar_contrato_externo", "excluir_contrato_externo", "simular_portabilidade", "simular_refinanciamento"],
  "Cadastro Contrato": ["acessar_cadastro_contrato", "cadastrar_contrato", "editar_contrato", "excluir_contrato"],
  "Bancos": ["acessar_bancos", "cadastrar_banco", "editar_banco", "excluir_banco", "gerenciar_instituicoes_banco"],
  "Tabelas de Comissão": ["acessar_tabelas_comissao", "cadastrar_tabela", "editar_tabela", "excluir_tabela", "importar_tabela"],
  "Órgãos": ["acessar_orgaos", "cadastrar_orgao", "editar_orgao", "excluir_orgao"],
  "Vendas": ["acessar_vendas", "cadastrar_venda", "editar_venda", "excluir_venda", "visualizar_comissoes"],
  "Financeiro": ["acessar_financeiro", "gerar_cobranca", "visualizar_pagamentos", "confirmar_pagamento_manual", "prorrogar_vencimento"],
  "Esteira de Clientes": ["acessar_esteira_cliente", "mover_cliente_esteira", "editar_status_esteira"],
  "Gestão de Contrato": ["acessar_esteira_contrato", "mover_contrato_esteira", "editar_status_contrato"],
  "Plano de Metas": ["acessar_metas", "cadastrar_meta", "editar_meta", "excluir_meta", "visualizar_progresso_metas"],
  "Dashboard de Leads": ["acessar_dashboard_leads", "importar_leads", "exportar_leads", "visualizar_analytics_leads"],
  "Relatórios": ["acessar_relatorios", "gerar_relatorio_empresas", "gerar_relatorio_usuarios", "gerar_relatorio_financeiro", "gerar_relatorio_produtividade", "exportar_relatorios"],
  "Gestão de Acesso - Usuários": ["acessar_usuarios", "cadastrar_usuario", "editar_usuario", "excluir_usuario", "listar_usuarios", "visualizar_status_usuario", "bloquear_usuario"],
  "Gestão de Acesso - Empresas": ["acessar_empresas", "cadastrar_empresa", "editar_empresa", "listar_empresas", "bloquear_desbloquear_empresa", "visualizar_detalhes_empresa"],
  "Gestão de Acesso - Funções": ["acessar_funcoes", "criar_funcao", "editar_funcao", "excluir_funcao", "listar_funcoes", "atribuir_permissao_a_funcao", "remover_permissao_de_funcao"],
  "Segurança": ["configurar_2fa", "visualizar_logs", "gerenciar_sessoes"],
  "Configurações": ["acessar_configuracoes", "editar_configuracoes_sistema", "gerenciar_integracao"]
};

export default function GestaoFuncoes() {
  const [funcoes, setFuncoes] = useState([]);
  const [permissoesSistema, setPermissoesSistema] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Usuário logado
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [empresaLogada, setEmpresaLogada] = useState(null);
  
  // Form
  const [formData, setFormData] = useState({
    nome: "",
    codigo: "",
    descricao: "",
    permissoes: [],
    empresa_id: null
  });

  useEffect(() => {
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
    const empresa = JSON.parse(localStorage.getItem("empresa") || "{}");
    setUsuarioLogado(usuario);
    setEmpresaLogada(empresa);
    carregarDados();
  }, []);

  // Filtrar categorias de permissões baseado no pacote da empresa
  const getCategoriasFiltradas = () => {
    // Se é Master Geral, mostra todas as permissões
    if (usuarioLogado?.perfil === "MASTER_GERAL") {
      return CATEGORIAS_PERMISSOES;
    }
    
    // Se a empresa tem permissões definidas, filtrar
    const permissoesEmpresa = empresaLogada?.permissoes_empresa || [];
    if (permissoesEmpresa.length === 0) {
      // Se empresa não tem pacote definido, mostrar tudo
      return CATEGORIAS_PERMISSOES;
    }
    
    // Filtrar categorias e permissões pela empresa
    const categoriasFiltradas = {};
    for (const [categoria, permissoes] of Object.entries(CATEGORIAS_PERMISSOES)) {
      const permissoesFiltradas = permissoes.filter(p => permissoesEmpresa.includes(p));
      if (permissoesFiltradas.length > 0) {
        categoriasFiltradas[categoria] = permissoesFiltradas;
      }
    }
    
    return categoriasFiltradas;
  };

  const carregarDados = async () => {
    setLoading(true);
    try {
      const empresa = JSON.parse(localStorage.getItem("empresa") || "{}");
      
      const [funcoesRes, permissoesRes] = await Promise.all([
        axios.get(`${API}/api/funcoes${empresa?.id ? `?empresa_id=${empresa.id}` : ""}`),
        axios.get(`${API}/api/permissoes`)
      ]);
      
      setFuncoes(funcoesRes.data.funcoes || []);
      setPermissoesSistema(permissoesRes.data.permissoes || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar funções");
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (funcao = null) => {
    if (funcao) {
      setEditando(funcao);
      setFormData({
        nome: funcao.nome || "",
        codigo: funcao.codigo || "",
        descricao: funcao.descricao || "",
        permissoes: funcao.permissoes || [],
        empresa_id: funcao.empresa_id || empresaLogada?.id || null
      });
    } else {
      setEditando(null);
      setFormData({
        nome: "",
        codigo: "",
        descricao: "",
        permissoes: [],
        empresa_id: empresaLogada?.id || null
      });
    }
    setModalOpen(true);
  };

  const salvarFuncao = async () => {
    if (!formData.nome || !formData.codigo) {
      toast.error("Nome e código são obrigatórios");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tipo: "CUSTOM"
      };
      
      if (editando) {
        await axios.put(`${API}/api/funcoes/${editando.id}`, payload);
        toast.success("Função atualizada com sucesso");
      } else {
        await axios.post(`${API}/api/funcoes`, payload);
        toast.success("Função criada com sucesso");
      }
      
      setModalOpen(false);
      carregarDados();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar função");
    } finally {
      setSaving(false);
    }
  };

  const excluirFuncao = async (funcao) => {
    if (funcao.tipo === "PADRAO") {
      toast.error("Não é possível excluir funções padrão do sistema");
      return;
    }
    
    if (!confirm(`Excluir função ${funcao.nome}?`)) return;
    
    try {
      await axios.delete(`${API}/api/funcoes/${funcao.id}`);
      toast.success("Função excluída");
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir");
    }
  };

  const togglePermissao = (permissao) => {
    setFormData(prev => {
      const novasPermissoes = prev.permissoes.includes(permissao)
        ? prev.permissoes.filter(p => p !== permissao)
        : [...prev.permissoes, permissao];
      return { ...prev, permissoes: novasPermissoes };
    });
  };

  const selecionarTodasCategoria = (categoria) => {
    const categorias = getCategoriasFiltradas();
    const permissoesCategoria = categorias[categoria] || [];
    setFormData(prev => {
      const todasSelecionadas = permissoesCategoria.every(p => prev.permissoes.includes(p));
      let novasPermissoes;
      if (todasSelecionadas) {
        // Remover todas da categoria
        novasPermissoes = prev.permissoes.filter(p => !permissoesCategoria.includes(p));
      } else {
        // Adicionar todas da categoria
        novasPermissoes = [...new Set([...prev.permissoes, ...permissoesCategoria])];
      }
      return { ...prev, permissoes: novasPermissoes };
    });
  };

  const funcoesFiltradas = funcoes.filter(f => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return f.nome?.toLowerCase().includes(search) || f.codigo?.toLowerCase().includes(search);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Funções e Permissões
          </h1>
          <p className="text-gray-500">Gerencie as funções e suas permissões</p>
        </div>
        
        <Button onClick={() => abrirModal()} className="gap-2">
          <Plus className="h-4 w-4" />
          Nova Função
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar função..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <Button variant="outline" size="icon" onClick={carregarDados}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Funções */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {funcoesFiltradas.map((funcao) => (
          <Card key={funcao.id} className={funcao.tipo === "PADRAO" ? "border-blue-200 bg-blue-50/30" : ""}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${funcao.tipo === "PADRAO" ? "bg-blue-100" : "bg-slate-100"}`}>
                    <Key className={`h-4 w-4 ${funcao.tipo === "PADRAO" ? "text-blue-600" : "text-slate-600"}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{funcao.nome}</CardTitle>
                    <Badge variant="outline" className="mt-1">{funcao.codigo}</Badge>
                  </div>
                </div>
                <Badge className={funcao.tipo === "PADRAO" ? "bg-blue-500" : "bg-slate-500"}>
                  {funcao.tipo === "PADRAO" ? "Padrão" : "Personalizada"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {funcao.descricao && (
                <p className="text-sm text-gray-500 mb-3">{funcao.descricao}</p>
              )}
              
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>{funcao.permissoes?.length || 0} permissões</span>
                </div>
                
                <div className="flex flex-wrap gap-1 max-h-20 overflow-hidden">
                  {funcao.permissoes?.slice(0, 5).map((perm) => (
                    <Badge key={perm} variant="outline" className="text-xs">
                      {perm.replace(/_/g, " ")}
                    </Badge>
                  ))}
                  {(funcao.permissoes?.length || 0) > 5 && (
                    <Badge variant="secondary" className="text-xs">
                      +{funcao.permissoes.length - 5} mais
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                <Button size="sm" variant="ghost" onClick={() => abrirModal(funcao)}>
                  <Edit className="h-4 w-4" />
                </Button>
                {funcao.tipo !== "PADRAO" && (
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => excluirFuncao(funcao)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editando ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editando ? "Editar Função" : "Nova Função"}
            </DialogTitle>
            <DialogDescription>
              {editando ? "Atualize as permissões da função" : "Configure a nova função e suas permissões"}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Nome da Função *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: Supervisor de Vendas"
                  disabled={editando?.tipo === "PADRAO"}
                />
              </div>
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                  placeholder="Ex: SUP"
                  maxLength={10}
                  disabled={editando?.tipo === "PADRAO"}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Descrição</Label>
                <Textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Descreva a função..."
                  rows={2}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Permissões ({formData.permissoes.length} selecionadas)
                </h4>
                {usuarioLogado?.perfil !== "MASTER_GERAL" && empresaLogada?.permissoes_empresa?.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Limitado pelo pacote da empresa
                  </Badge>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Object.entries(getCategoriasFiltradas()).map(([categoria, permissoes]) => {
                  const todasSelecionadas = permissoes.every(p => formData.permissoes.includes(p));
                  const algumaSelecionada = permissoes.some(p => formData.permissoes.includes(p));
                  
                  return (
                    <Card key={categoria} className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-semibold text-sm">{categoria}</Label>
                        <Checkbox
                          checked={todasSelecionadas}
                          onCheckedChange={() => selecionarTodasCategoria(categoria)}
                          className={algumaSelecionada && !todasSelecionadas ? "data-[state=checked]:bg-gray-400" : ""}
                        />
                      </div>
                      <div className="space-y-1">
                        {permissoes.map((perm) => (
                          <div key={perm} className="flex items-center gap-2">
                            <Checkbox
                              id={perm}
                              checked={formData.permissoes.includes(perm)}
                              onCheckedChange={() => togglePermissao(perm)}
                            />
                            <label htmlFor={perm} className="text-xs text-gray-600 cursor-pointer">
                              {perm.replace(/_/g, " ")}
                            </label>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={salvarFuncao} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
